import { isString } from 'lodash'
import React from 'react'
import PropTypes from 'prop-types'

import Navigation from 'utils/navigation'

// nice color props that can be passed as a bare key
const COLOR_PROPS = ['blue', 'gray', 'red', 'green', 'white', 'gold']

const colorPropsToClasses = props => COLOR_PROPS.reduce(
    (acc, color) => {
      if(props[color]) {
        acc += ` ${color}`
      }

      return acc
    }
  , "")

// supports route paths and functions
// TODO: doesn't work with a file download link or external URLs
const actionToOnClick = action => {
  return isString(action)
    ? () => Navigation.go(action) // strings are paths
    : action
}

// allows easy icon props without all the fa- prefixing
const iconNameToElement = (icon, defaultPrefix='fa-', defaultSet='fas') => {
  if(!icon) { return }

  const
    iconParts = icon.split(' '),
    iconSet = (iconParts.length === 2)
      ? iconParts[0]
      : defaultSet,
    iconName = iconParts[1] || iconParts[0],
    iconClassName = iconName.startsWith("fa-")
      ? `${iconSet} ${iconName}`
      : `${iconSet} ${defaultPrefix}${icon}`

  if(iconParts.length > 2) { throw new Error(`iconNameToElement received an icon with more than 1 space: ${icon}`) }

  return <i className={ iconClassName } />
}

// enforce whether label and/or icon are present, or children, never both
const propsToChildren = ({ children, label, icon }, noChildrenElement) => {
  if(children && (label || icon)) { throw new Error(`A Button was passed children AND a label or icon prop. Choose one style or the other.`) }

  return children || noChildrenElement
}

export const
  TinyIconButton = props => {
    const
      { icon, action, tooltip } = props,
      iconElement = iconNameToElement(icon, 'fa-circle-'),
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action)

    return <a title={ tooltip } className={ `tiny-icon-button ${ colorClasses }` } onClick={ doAction }>
        { iconElement }
    </a>
  },

  LargeIconButton = props => {
    const
      { icon, action, tooltip } = props,
      iconElement = iconNameToElement(icon),
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action)

    return <button title={ tooltip } className={ `btn btn-icon ${ colorClasses }` } onClick={ doAction }>
      { iconElement }
    </button>
  },

  RoundedButton = props => {
    const
      { label, icon, action } = props,
      iconElement = iconNameToElement(icon, 'fa-circle-'),
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action),
      children = propsToChildren(props, <>{ iconElement } { label }</>)

    return <button className={ `btn rounded ${colorClasses}` } onClick={ doAction }>
      { children }
    </button>
  },

  SquareButton = props => {
    const
      { label, icon, action } = props,
      iconElement = iconNameToElement(icon, 'fa-circle-'),
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action),
      children = propsToChildren(props, <>{ iconElement } { label }</>)

    return <button className={ `btn btn-square ${colorClasses}` } onClick={ doAction }>
      { children }
    </button>
  },

  SmallButton3d = props => {
    const
      { label, action } = props,
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action),
      children = propsToChildren(props, <>{ label }</>)

    return <button className={ `btn btn-3d-small ${colorClasses}` } onClick={ doAction }>
      { children }
    </button>
  },

  Button3d = props => {
    const
      { label, action } = props,
      colorClasses = colorPropsToClasses(props),
      doAction = actionToOnClick(action),
      children = propsToChildren(props, <>{ label }</>)

    return <button className={ `btn btn-3d ${colorClasses}` } onClick={ doAction }>
      { children }
    </button>
  }

// Set proptypes on all buttons
const
  actionPropType = { action: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string
  ])},
  iconPropType = { icon: PropTypes.string },
  labelPropType = { label: PropTypes.node },
  tooltipPropType = { tooltip: PropTypes.string },
  colorPropTypes = COLOR_PROPS.reduce((acc, color) => {
    acc[color] = PropTypes.bool
    return acc
  }, {})

TinyIconButton.propTypes = {
  ...actionPropType,
  icon: iconPropType.icon.isRequired,
  tooltip: tooltipPropType.tooltip.isRequired,
}

LargeIconButton.propTypes = {
  ...actionPropType,
  icon: iconPropType.icon.isRequired,
  tooltip: tooltipPropType.tooltip.isRequired,
  ...colorPropTypes
}

RoundedButton.propTypes = {
  ...actionPropType,
  ...iconPropType,
  ...labelPropType,
  ...colorPropTypes
}

SquareButton.propTypes = {
  ...actionPropType,
  ...labelPropType,
  ...colorPropTypes
}

SmallButton3d.propTypes = {
  ...actionPropType,
  ...labelPropType,
  ...colorPropTypes
}

Button3d.propTypes = {
  ...actionPropType,
  ...labelPropType,
  ...colorPropTypes
}
